/**
 * ダッシュボードページ
 *
 * アプリのホーム画面。ユーザーの習慣達成状況を一覧表示する。
 *
 * 構成要素:
 * - ProgressHeader: 今日の進捗率とプログレスバー
 * - TodayHabits: 今日予定されている習慣のチェックリスト
 * - WeeklySummary: 週間の達成状況カレンダー
 * - StreakCard: 連続達成日数の表示
 * - WeeklyStats: 今週の統計サマリー
 *
 * Rails で言えば:
 * - app/controllers/dashboard_controller.rb#index のビューに相当
 * - データは Inertia.js 経由で props として受け取る
 */

import { useState, useCallback, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { HabitLayout } from '@/components/habits/HabitLayout'
import { HabitForm } from '@/components/habits/HabitForm'
import { HabitLogModal } from '@/components/habits/HabitLogModal'
import { SatisfactionPicker } from '@/components/satisfaction'
import { useHabitToggle } from '@/hooks/useHabitToggle'
import type { TodayHabit, WeeklyStat, Streak, PageProps } from '@/types'

// 子コンポーネント
import {
  ProgressHeader,
  TodayHabits,
  WeeklySummary,
  StreakCard,
  WeeklyStats,
} from './components'

/**
 * ダッシュボードページの Props
 * Rails コントローラーから Inertia 経由で渡される
 */
interface DashboardProps extends PageProps {
  todayHabits?: TodayHabit[]
  weeklyStats?: WeeklyStat[]
  streaks?: Streak[]
  today: string // サーバー側でユーザーのタイムゾーンに基づいて計算された「今日」
  todaySatisfaction?: number | null
}

export default function DashboardIndex({
  todayHabits = [],
  weeklyStats = [],
  streaks = [],
  today, // サーバーから提供される「今日」を使用
  todaySatisfaction = null,
}: DashboardProps) {
  // === 状態管理 ===
  const [habits, setHabits] = useState(todayHabits)
  const [showForm, setShowForm] = useState(false)

  // props の変更を監視してローカル状態を同期
  useEffect(() => {
    setHabits(todayHabits)
  }, [todayHabits])

  // === カスタムフック ===
  const {
    toggle,
    isPending,
    logModalTarget,
    closeLogModal,
    saveLog,
    uncomplete,
  } = useHabitToggle({
    onError: (error) => console.error('Toggle failed:', error),
  })

  // === 計算値 ===
  const scheduledHabits = habits.filter((h) => h.scheduledToday)
  const completedCount = scheduledHabits.filter((h) => h.completedToday).length
  const totalCount = scheduledHabits.length
  const progressPercent =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  // === イベントハンドラー ===
  const handleToggle = useCallback(
    (habitId: number) => {
      const habit = habits.find((h) => h.id === habitId)
      if (!habit) return

      toggle(
        habitId,
        today,
        !!habit.completedToday,
        (completed) => {
          setHabits((prev) =>
            prev.map((h) =>
              h.id === habitId ? { ...h, completedToday: completed } : h
            )
          )
        },
        {
          enabled: !!habit.loggingEnabled,
          habitName: habit.name,
          existingLog: habit.todayLog,
        }
      )
    },
    [habits, today, toggle]
  )

  // pendingToggles を TodayHabits コンポーネント用に変換
  const pendingToggles = habits.reduce<Record<number, boolean>>((acc, h) => {
    if (isPending(h.id, today)) acc[h.id] = true
    return acc
  }, {})

  // === レンダリング ===
  return (
    <>
      <HabitLayout
        title="ホーム"
        action={
          <Button onClick={() => setShowForm(true)} className="touch-target">
            <Plus className="mr-1 h-5 w-5" />
            <span className="hidden sm:inline">新しい習慣</span>
          </Button>
        }
      >
        <div className="mx-auto max-w-7xl">
          {/* === 進捗ヘッダー === */}
          <ProgressHeader
            progressPercent={progressPercent}
            completedCount={completedCount}
            totalCount={totalCount}
          />

          {/* === 満足度バロメーター === */}
          <SatisfactionPicker value={todaySatisfaction} today={today} />

          {/* === メインコンテンツ === */}
          <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
            {/* メインカラム: 今日の習慣と週間サマリー */}
            <div className="space-y-4 lg:col-span-2">
              <TodayHabits
                habits={scheduledHabits}
                pendingToggles={pendingToggles}
                onToggle={handleToggle}
              />
              <WeeklySummary weeklyStats={weeklyStats} today={today} />
            </div>

            {/* サイドカラム: ストリークと統計 */}
            <div className="space-y-4">
              <StreakCard streaks={streaks} />
              <WeeklyStats weeklyStats={weeklyStats} />
            </div>
          </div>
        </div>
      </HabitLayout>

      {/* Form Modal */}
      {showForm && <HabitForm onClose={() => setShowForm(false)} />}

      {/* Log Modal */}
      {logModalTarget && (
        <HabitLogModal
          habitName={logModalTarget.habitName}
          initialData={logModalTarget.existingLog}
          mode={logModalTarget.mode}
          onSave={(data) =>
            saveLog(logModalTarget.habitId, logModalTarget.date, data)
          }
          onSkip={closeLogModal}
          onUncomplete={
            logModalTarget.mode === 'edit'
              ? () => {
                  const { habitId } = logModalTarget
                  uncomplete(habitId, today, (completed) => {
                    setHabits((prev) =>
                      prev.map((h) =>
                        h.id === habitId
                          ? { ...h, completedToday: completed }
                          : h
                      )
                    )
                  })
                }
              : undefined
          }
        />
      )}
    </>
  )
}
